import React, { useState, useMemo, createContext, useContext, useCallback } from "react";
import "./Toast.css";

const ToastContext = createContext();
export const useToast = () => useContext(ToastContext);

const ToastContainer = ({
    children,
    position = "top-right",  // top-right, top-left, top-center, bottom-right, bottom-left, bottom-center
    stacked = false,
    autoClose = 5000,       // default auto-close for all toasts e.g. 5 secs
    closeButton = true,     // show/hide close button
    pauseOnHover = true,    // default pause on hover
    newestOnTop = false,     // default newest toast on top
    theme = "light",         // light, dark, colored
    hideProgressBar = false,  // default hide the progress bar 
}) => {
    const [toasts, setToasts] = useState([]);

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    const removeAllToasts = useCallback(() => {
        setToasts([]);
    }, []);

    const addToast = useCallback(
        (message, type = "default", options = {}) => {
            const id = Date.now() + Math.random();
            const toast = {
                id,
                message,
                type,
                autoClose: options.autoClose !== undefined ? options.autoClose : autoClose,
                closeButton: options.closeButton !== undefined ? options.closeButton : closeButton,
                pauseOnHover: options.pauseOnHover !== undefined ? options.pauseOnHover : pauseOnHover,
                hideProgressBar: options.hideProgressBar !== undefined ? options.hideProgressBar : hideProgressBar,
                theme: options.theme !== undefined ? options.theme : theme,
                ...options
            };

            // if newestOnTop = true, append newest toast to the front, otherwise, append to the back
            setToasts((prev) => newestOnTop ? [toast, ...prev] : [...prev, toast]);

            // if (toast.autoClose) {
            //     setTimeout(() => removeToast(id), toast.autoClose);
            // }

            return id;
        },
        [autoClose, closeButton, pauseOnHover, newestOnTop, hideProgressBar, theme]
    );

    const toast = useMemo(() => ({
        success: (msg, opts) => addToast(msg, "success", opts),
        error: (msg, opts) => addToast(msg, "error", opts),
        info: (msg, opts) => addToast(msg, "info", opts),
        warning: (msg, opts) => addToast(msg, "warning", opts),
        default: (msg, opts) => addToast(msg, "default", opts),
        dismiss: (id) => {
            if (id) removeToast(id);
            else removeAllToasts();
        },
    }), [addToast, removeToast, removeAllToasts]);

    return (
        // Pass toast down the tree to children
        <ToastContext.Provider value={toast}>
            {children}
            <div className={`toast-container ${position}`} >
                {toasts.map((toast, index) => (
                    <Toast 
                        key={toast.id} 
                        toast={toast} 
                        onClose={() => {
                            // e.stopPropagation(); // stops click from reaching the parent
                            removeToast(toast.id)
                        }} 
                        stacked={stacked} 
                        theme={theme}
                    />
                ))}
            </div>
        </ToastContext.Provider>
    );
};

const Toast = ({ toast, onClose, stacked }) => {
    const typeClass = `toast toast-${toast.type} ${stacked ? 'stacked' : ''} ${toast.theme}`;

    return (
        <div className={`${typeClass} animate-slideIn`} onClick={toast.onClick}>
            <div className={`toast-content ${toast.theme}`}>
                <span className="toast-icon">
                    {toast.type === "success" && <i className="fa-solid fa-circle-check" style={{color: '#22BE63'}}></i>}
                    {toast.type === "error" && <i className="fa-solid fa-circle-exclamation" style={{color: '#C82333'}}></i>}
                    {toast.type === "warning" && <i className="fa-solid fa-triangle-exclamation" style={{color: '#FFD43B'}}></i>}
                    {toast.type === "info" && <i className="fa-solid fa-circle-exclamation" style={{color: '#4E9CED'}}></i>}
                </span>
                <div className="toast-message">{toast.message}</div>
                {toast.closeButton && (
                <button className={`toast-close ${toast.theme}`} onClick={onClose}>
                    <i className="fa-solid fa-xmark"></i>
                </button>
                )}
            </div>
            {toast.autoClose && (
                <div className="toast-progress">
                <div
                    className={`toast-progress-bar toast-progress-${toast.type} ${toast.hideProgressBar ? 'hidden' : ''} ${toast.pauseOnHover ? 'pause' : '' }`}
                    style={{ animationDuration: `${toast.autoClose}ms` }}
                    onAnimationEnd={() => onClose()} // triggers when width reaches 0%
                />
                </div>
            )}
        </div>
    );
};

export default ToastContainer;